import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, getErrorMessage } from "@/lib/api";
import { portalPath, useAuth } from "@/context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [busy, setBusy] = useState(false);

  const requestOtp = async () => {
    if (!/^[0-9]{10}$/.test(mobile)) return toast.error("Please enter a valid 10-digit mobile number");
    setBusy(true);
    try {
      const res = await api.post("/auth/request-otp", { mobile });
      setDevOtp(res.data.dev_otp);
      setStep(2);
      toast.success("OTP sent to your mobile number");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) return toast.error("Please enter the 6-digit OTP");
    setBusy(true);
    try {
      const res = await api.post("/auth/verify-otp", { mobile, otp });
      login(res.data.token, res.data.user);
      toast.success(`Welcome back, ${res.data.user.name}!`);
      navigate(portalPath(res.data.user.role));
    } catch (err) {
      toast.error(getErrorMessage(err));
      if (err.response?.status === 404) {
        setTimeout(() => navigate("/jobs"), 1800);
      } else {
        setOtp("");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-14">
      <div className="w-full max-w-sm">
        <div className="text-center mb-7">
          <span className="inline-flex h-12 w-12 rounded-2xl bg-primary/10 text-primary items-center justify-center mb-4">
            <Smartphone className="h-6 w-6" />
          </span>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">Log in with OTP</h1>
          <p className="text-sm text-muted-foreground mt-1">Your mobile number is your identity. No passwords.</p>
        </div>

        <Card className="rounded-xl border-border shadow-[var(--shadow-sm)]">
          <CardContent className="p-6">
            {step === 1 ? (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="login-mobile">Mobile number</Label>
                  <Input
                    id="login-mobile"
                    data-testid="login-mobile-input"
                    placeholder="10-digit mobile number"
                    inputMode="numeric"
                    maxLength={10}
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                    onKeyDown={(e) => e.key === "Enter" && requestOtp()}
                  />
                </div>
                <Button onClick={requestOtp} disabled={busy} className="w-full rounded-lg" data-testid="login-send-otp-button">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Send OTP
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground text-center">
                  OTP sent to <span className="font-mono font-medium text-foreground">{mobile}</span>
                </p>
                <div className="flex flex-col items-center gap-3">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="login-otp-input">
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                    </InputOTPGroup>
                  </InputOTP>
                  {devOtp && (
                    <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-1.5" data-testid="login-dev-otp-chip">
                      <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                      <span className="text-xs text-emerald-700">Dev mode OTP:</span>
                      <span className="font-mono text-sm font-semibold text-emerald-800">{devOtp}</span>
                    </div>
                  )}
                </div>
                <Button onClick={verifyOtp} disabled={busy} className="w-full rounded-lg" data-testid="otp-verify-button">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Verify & Log in
                </Button>
                <Button variant="ghost" onClick={() => { setStep(1); setOtp(""); }} className="w-full rounded-lg" data-testid="login-back-button">
                  Change number
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
